import React, { useState } from "react";
import axios from "axios";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaCalendarAlt, FaSpinner } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function to format currency (consistent with previous components)
const formatCurrency = (amount) => {
  const numericAmount = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-KE', { 
    style: 'currency', 
    currency: 'Ksh', 
    minimumFractionDigits: 0 
  }).format(numericAmount).replace('Ksh', 'Ksh ');
};

const MonthlyFeesPage = () => {
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [fees, setFees] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFees(null); // Clear previous results on new submission

    // Basic input validation
    if (!year || !month || month < 1 || month > 12) {
      toast.error("Please enter a valid year and a month (1-12).");
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("token"); // Retrieve bearer token from local storage
      const response = await axios.get(
        `${StandardURL}/iNyumba/money/MonthlyFees?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Ensure the fee is a number, even if API response is null
      setFees(response.data.response || 0); 

      if (response.data.response === 0) {
         toast.info(`No fees found for ${month}/${year}.`);
      } else {
         toast.success(`Successfully fetched fees for ${month}/${year}.`);
      }

    } catch (error) {
      console.error("Error fetching monthly fees:", error);
      // Use optional chaining for safer access to nested properties
      const errorMessage = error.response?.data?.response || "An unexpected error occurred.";
      toast.error(errorMessage);
      setFees(null); // Set fees to null on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
           
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center mb-8 border-b pb-4">
                <FaCalendarAlt className="mr-3 text-indigo-600" /> Monthly Fee Report
            </h1>

            <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
                <p className="text-lg text-gray-600 mb-6">
                    Enter the desired year and month to retrieve the total fees collected for that period.
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                    
                    <div className="flex-grow">
                        <label htmlFor="yearInput" className="block text-sm font-medium text-gray-700 mb-1">
                            Year (e.g., 2024)
                        </label>
                        <input
                            type="number"
                            id="yearInput"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition duration-150"
                            placeholder="YYYY"
                            min="2000"
                            max={(new Date().getFullYear() + 1)}
                        />
                    </div>
                    
                    <div className="flex-grow">
                        <label htmlFor="monthInput" className="block text-sm font-medium text-gray-700 mb-1">
                            Month (1 to 12)
                        </label>
                        <input
                            type="number"
                            id="monthInput"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            min="1"
                            max="12"
                            required
                            className="w-full border border-gray-300 rounded-lg py-2.5 px-4 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition duration-150"
                            placeholder="MM"
                        />
                    </div>
                    
                    <button
                        type="submit"
                        className="sm:mt-6 w-full sm:w-auto inline-flex items-center justify-center rounded-lg border border-transparent shadow-md px-6 py-2.5 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <FaSpinner className="animate-spin mr-2" />
                                Loading...
                            </div>
                        ) : (
                            <>
                                <FaSearch className="mr-2" />
                                Get Fees
                            </>
                        )}
                    </button>
                </form>
            </div>


            {/* Result Display Card */}
            {fees !== null && (
                <div className="bg-white p-8 rounded-xl shadow-2xl border-l-4 border-green-500 mt-8">
                    <h2 className="text-xl font-medium text-gray-500 flex items-center mb-4">
                        <FaCalendarAlt className="w-5 h-5 mr-2 text-green-500" />
                        Total Monthly Fees Collected
                    </h2>
                    <p className="text-sm uppercase tracking-wider text-gray-700">
                        For {month} / {year}
                    </p>
                    <p className="text-4xl font-extrabold text-gray-900 mt-2">
                        {formatCurrency(fees)}
                    </p>
                </div>
            )}

            {/* Initial loading state if logic was changed to load on mount, but kept empty for clarity */}
            {loading && fees === null && (
                <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-xl mt-8">
                    <Oval color="#4f46e5" secondaryColor="#a5b4fc" height={50} width={50} ariaLabel="loading-spinner" />
                    <p className="mt-4 text-gray-600">Retrieving data for the selected period...</p>
                </div>
            )}
            
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default MonthlyFeesPage;